/**
 * Plausibilitäts-Schwellwerte für den Forderungsmanager (Issue #268).
 *
 * Schwellwerte werden als Einzel-Zeilen in `app_settings` gehalten (ein
 * Key pro Wert — gleiches Pattern wie `kiosk_pin`). Die Plausibilitäts-
 * Logik (Issue #267 bzw. Frontend-Fallback aus #269) liest diese Werte,
 * die Defaults kommen aus der Seed-Migration.
 *
 * Semantik:
 *  - Schadentag / Abrechnung: `gruen_max < gelb_max`. Werte ≤ gruen_max
 *    sind grün, bis gelb_max gelb, darüber rot.
 *  - Verjährung: `rot_max < gelb_max`. Werte unter rot_max sind rot
 *    (zu wenig Zeit bis zur Verjährung), unter gelb_max gelb, darüber grün.
 */

import { supabase } from './supabase';

export type PlausiThresholdKey =
  | 'plausi_schadentag_gruen_max'
  | 'plausi_schadentag_gelb_max'
  | 'plausi_abrechnung_gruen_max'
  | 'plausi_abrechnung_gelb_max'
  | 'plausi_verjaehrung_rot_max'
  | 'plausi_verjaehrung_gelb_max';

export type PlausiThresholds = Record<PlausiThresholdKey, number>;

export const PLAUSI_SETTING_KEYS: PlausiThresholdKey[] = [
  'plausi_schadentag_gruen_max',
  'plausi_schadentag_gelb_max',
  'plausi_abrechnung_gruen_max',
  'plausi_abrechnung_gelb_max',
  'plausi_verjaehrung_rot_max',
  'plausi_verjaehrung_gelb_max',
];

export const DEFAULT_PLAUSI_THRESHOLDS: PlausiThresholds = {
  plausi_schadentag_gruen_max: 14,
  plausi_schadentag_gelb_max: 30,
  plausi_abrechnung_gruen_max: 30,
  plausi_abrechnung_gelb_max: 90,
  plausi_verjaehrung_rot_max: 90,
  plausi_verjaehrung_gelb_max: 180,
};

export interface PlausiThresholdLabel {
  key: PlausiThresholdKey;
  gruppe: 'schadentag' | 'abrechnung' | 'verjaehrung';
  stufe: 'gruen_max' | 'gelb_max' | 'rot_max';
  label: string;
  beschreibung: string;
}

export const PLAUSI_THRESHOLD_LABELS: PlausiThresholdLabel[] = [
  {
    key: 'plausi_schadentag_gruen_max',
    gruppe: 'schadentag',
    stufe: 'gruen_max',
    label: 'Schadentag ↔ Mietbeginn — Grün',
    beschreibung:
      'Differenz in Tagen zwischen Schadendatum und Mietbeginn bis zu der die Ampel grün ist.',
  },
  {
    key: 'plausi_schadentag_gelb_max',
    gruppe: 'schadentag',
    stufe: 'gelb_max',
    label: 'Schadentag ↔ Mietbeginn — Gelb',
    beschreibung: 'Obergrenze in Tagen für „gelb". Darüber wird die Ampel rot.',
  },
  {
    key: 'plausi_abrechnung_gruen_max',
    gruppe: 'abrechnung',
    stufe: 'gruen_max',
    label: 'Mietende ↔ Rechnungsdatum — Grün',
    beschreibung:
      'Differenz in Tagen zwischen Mietende und Rechnungsdatum bis zu der die Ampel grün ist.',
  },
  {
    key: 'plausi_abrechnung_gelb_max',
    gruppe: 'abrechnung',
    stufe: 'gelb_max',
    label: 'Mietende ↔ Rechnungsdatum — Gelb',
    beschreibung: 'Obergrenze in Tagen für „gelb". Darüber wird die Ampel rot.',
  },
  {
    key: 'plausi_verjaehrung_rot_max',
    gruppe: 'verjaehrung',
    stufe: 'rot_max',
    label: 'Verjährung — Rot unterhalb (Tage)',
    beschreibung:
      'Verbleiben weniger Tage bis zur Verjährung, wird die Ampel rot.',
  },
  {
    key: 'plausi_verjaehrung_gelb_max',
    gruppe: 'verjaehrung',
    stufe: 'gelb_max',
    label: 'Verjährung — Gelb unterhalb (Tage)',
    beschreibung:
      'Verbleiben weniger Tage bis zur Verjährung (aber mehr als „Rot"), wird die Ampel gelb.',
  },
];

export interface PlausiValidationResult {
  ok: boolean;
  errors: Partial<Record<PlausiThresholdKey, string>>;
}

export function validatePlausiThresholds(
  values: PlausiThresholds,
): PlausiValidationResult {
  const errors: Partial<Record<PlausiThresholdKey, string>> = {};

  for (const key of PLAUSI_SETTING_KEYS) {
    const v = values[key];
    if (!Number.isInteger(v) || v < 0) {
      errors[key] = 'Wert muss eine ganze Zahl ≥ 0 sein.';
    }
  }

  if (
    values.plausi_schadentag_gelb_max <= values.plausi_schadentag_gruen_max &&
    !errors.plausi_schadentag_gelb_max
  ) {
    errors.plausi_schadentag_gelb_max =
      'Gelb muss größer als Grün sein (Schadentag).';
  }

  if (
    values.plausi_abrechnung_gelb_max <= values.plausi_abrechnung_gruen_max &&
    !errors.plausi_abrechnung_gelb_max
  ) {
    errors.plausi_abrechnung_gelb_max =
      'Gelb muss größer als Grün sein (Abrechnung).';
  }

  if (
    values.plausi_verjaehrung_rot_max >= values.plausi_verjaehrung_gelb_max &&
    !errors.plausi_verjaehrung_rot_max
  ) {
    errors.plausi_verjaehrung_rot_max =
      'Rot muss kleiner als Gelb sein (Verjährung — kürzere Restfrist = schlimmer).';
  }

  return { ok: Object.keys(errors).length === 0, errors };
}

/**
 * Lädt die konfigurierten Plausi-Schwellwerte aus `app_settings`.
 *
 * Fehlende Keys (z. B. direkt nach einem Restore) werden mit den Defaults
 * aufgefüllt — so bleibt die Plausibilitätsprüfung auch ohne Seed funktional.
 */
export async function getPlausiThresholds(): Promise<PlausiThresholds> {
  const { data, error } = await supabase
    .from('app_settings')
    .select('key, value')
    .in('key', PLAUSI_SETTING_KEYS);

  if (error) {
    throw new Error(`Plausi-Schwellwerte laden fehlgeschlagen: ${error.message}`);
  }

  const result: PlausiThresholds = { ...DEFAULT_PLAUSI_THRESHOLDS };
  for (const row of (data ?? []) as Array<{ key: string; value: string | null }>) {
    if (!PLAUSI_SETTING_KEYS.includes(row.key as PlausiThresholdKey)) continue;
    const n = Number(row.value);
    if (Number.isFinite(n)) {
      result[row.key as PlausiThresholdKey] = n;
    }
  }
  return result;
}

/**
 * Speichert die sechs Schwellwerte als Upsert in `app_settings`.
 * Validiert vorher — bei inkonsistenten Werten wird nichts geschrieben.
 */
export async function savePlausiThresholds(
  values: PlausiThresholds,
): Promise<void> {
  const validation = validatePlausiThresholds(values);
  if (!validation.ok) {
    throw new Error(
      'Schwellwerte sind inkonsistent: ' +
        Object.values(validation.errors).filter(Boolean).join(' '),
    );
  }

  const rows = PLAUSI_SETTING_KEYS.map((key) => ({
    key,
    value: String(values[key]),
  }));

  const { error } = await supabase
    .from('app_settings')
    .upsert(rows, { onConflict: 'key' });

  if (error) {
    throw new Error(
      `Plausi-Schwellwerte speichern fehlgeschlagen: ${error.message}`,
    );
  }
}
